import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Skeleton } from "@/components/ui/skeleton"
import { 
  TrendingUp, 
  TrendingDown, 
  Clock, 
  MapPin, 
  DollarSign, 
  Users, 
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Plane,
  CalendarDays,
  BarChart3,
  Lightbulb,
  Bell
} from 'lucide-react'
import type { FlightAnalysis, FlightSearchParamsForAnalysis } from "@/types/travel"
import { cn } from "@/lib/utils"

interface FlightAnalysisTabProps {
  analysis?: FlightAnalysis | null
  searchParams?: FlightSearchParamsForAnalysis
  isLoading?: boolean
}

// Recommendation badge colors and styles
const recommendationStyles = {
  BUY_NOW: {
    bg: 'bg-emerald-500/10 dark:bg-emerald-500/20',
    border: 'border-emerald-500/30',
    text: 'text-emerald-700 dark:text-emerald-400',
    badgeBg: 'bg-emerald-500',
    icon: CheckCircle,
    label: 'Book Now',
  },
  MONITOR: {
    bg: 'bg-amber-500/10 dark:bg-amber-500/20',
    border: 'border-amber-500/30',
    text: 'text-amber-700 dark:text-amber-400',
    badgeBg: 'bg-amber-500',
    icon: Clock,
    label: 'Monitor Prices',
  },
  WAIT: {
    bg: 'bg-rose-500/10 dark:bg-rose-500/20',
    border: 'border-rose-500/30',
    text: 'text-rose-700 dark:text-rose-400',
    badgeBg: 'bg-rose-500',
    icon: AlertCircle,
    label: 'Wait to Book',
  },
}

// Price position indicator
const pricePositionStyles = {
  low: { color: 'text-emerald-600 dark:text-emerald-400', label: 'Below Average' },
  average: { color: 'text-amber-600 dark:text-amber-400', label: 'Average' },
  high: { color: 'text-rose-600 dark:text-rose-400', label: 'Above Average' },
}

// Competition level indicator
const competitionStyles = {
  high: { color: 'text-emerald-600 dark:text-emerald-400', label: 'High Competition' },
  medium: { color: 'text-amber-600 dark:text-amber-400', label: 'Medium Competition' },
  low: { color: 'text-rose-600 dark:text-rose-400', label: 'Low Competition' },
}

export function FlightAnalysisTab({ 
  analysis, 
  searchParams,
  isLoading = false 
}: FlightAnalysisTabProps) {
  
  // Loading skeleton
  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-24 w-full rounded-xl" />
      </div>
    )
  }
  
  // No data state
  if (!analysis) {
    return (
      <div className="text-center py-8">
        <Plane className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-sm text-muted-foreground">
          Flight analysis will appear here once search results are available
        </p>
      </div>
    )
  }

  const recStyle = recommendationStyles[analysis.recommendation.action]
  const RecIcon = recStyle.icon
  const priceStyle = pricePositionStyles[analysis.priceTiming.pricePosition]
  const compStyle = competitionStyles[analysis.marketTrends.competitionLevel]
  const hasChildren = searchParams?.passengers?.children && searchParams.passengers.children > 0

  return (
    <div className="space-y-4">
      {/* Recommendation Badge - Prominent Header */}
      <Card className={cn(
        "border-2 transition-all",
        recStyle.bg,
        recStyle.border
      )}>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={cn(
                "p-2 rounded-full",
                recStyle.badgeBg
              )}>
                <RecIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <Badge className={cn(
                  "text-sm font-bold px-3 py-1",
                  recStyle.badgeBg,
                  "text-white"
                )}>
                  {recStyle.label}
                </Badge>
                <p className={cn("text-sm mt-1 font-medium", recStyle.text)}>
                  {analysis.recommendation.reasoning}
                </p>
              </div>
            </div>
          </div>
          
          {/* Triggers */}
          {analysis.recommendation.triggers.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {analysis.recommendation.triggers.map((trigger, idx) => (
                <Badge 
                  key={idx} 
                  variant="secondary" 
                  className="text-xs rounded-full"
                >
                  {trigger}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Price Timing Analysis */}
      <Card className="bg-white dark:bg-zinc-800/50 backdrop-blur-sm border-gray-200 dark:border-zinc-700/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 text-gray-900 dark:text-white">
            <BarChart3 className="h-4 w-4 text-blue-500 dark:text-blue-400" />
            Price Timing Analysis
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Booking Window</p>
              <div className="flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">
                  {analysis.priceTiming.bookingWindow.current}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Optimal: {analysis.priceTiming.bookingWindow.optimal}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Price Position</p>
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <span className={cn("text-sm font-medium", priceStyle.color)}>
                  {priceStyle.label}
                </span>
              </div>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                {analysis.priceTiming.trend === 'increasing' && (
                  <>
                    <TrendingUp className="h-3 w-3 text-rose-500" />
                    <span>Prices Rising</span>
                  </>
                )}
                {analysis.priceTiming.trend === 'decreasing' && (
                  <>
                    <TrendingDown className="h-3 w-3 text-emerald-500" />
                    <span>Prices Falling</span>
                  </>
                )}
                {analysis.priceTiming.trend === 'stable' && (
                  <>
                    <ArrowRight className="h-3 w-3" />
                    <span>Prices Stable</span>
                  </>
                )}
              </div>
            </div>
          </div>
          
          {analysis.priceTiming.analysis && (
            <p className="text-sm text-gray-600 dark:text-gray-400 pt-2 border-t border-gray-100 dark:border-zinc-700/50">
              {analysis.priceTiming.analysis}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Market Trends */}
      <Card className="bg-white dark:bg-zinc-800/50 backdrop-blur-sm border-gray-200 dark:border-zinc-700/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 text-gray-900 dark:text-white">
            <TrendingUp className="h-4 w-4 text-blue-500 dark:text-blue-400" />
            Market Trends
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {analysis.marketTrends.routeCharacteristics}
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={cn("text-xs", compStyle.color)}>
              {compStyle.label}
            </Badge>
          </div>
          
          {analysis.marketTrends.seasonalFactors.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground font-medium">Seasonal Factors</p>
              <div className="flex flex-wrap gap-1">
                {analysis.marketTrends.seasonalFactors.map((factor, idx) => (
                  <Badge key={idx} variant="secondary" className="text-xs">
                    {factor}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Alternative Strategies */}
      {(analysis.alternatives.dateFlexibility?.length || 
        analysis.alternatives.alternativeAirports?.length || 
        analysis.alternatives.routingOptions?.length) && (
        <Card className="bg-white dark:bg-zinc-800/50 backdrop-blur-sm border-gray-200 dark:border-zinc-700/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-gray-900 dark:text-white">
              <Lightbulb className="h-4 w-4 text-yellow-500 dark:text-yellow-400" />
              Alternative Strategies
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Date Flexibility */}
            {analysis.alternatives.dateFlexibility && analysis.alternatives.dateFlexibility.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                  <CalendarDays className="h-3 w-3" />
                  Flexible Dates
                </p>
                <div className="space-y-1">
                  {analysis.alternatives.dateFlexibility.map((option, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm bg-gray-50 dark:bg-zinc-900/50 rounded-lg px-3 py-2">
                      <span>{option.date}</span>
                      <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                        Save ${option.savings} ({option.percentage}%)
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Alternative Airports */}
            {analysis.alternatives.alternativeAirports && analysis.alternatives.alternativeAirports.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  Alternative Airports
                </p>
                <div className="space-y-1">
                  {analysis.alternatives.alternativeAirports.map((option, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm bg-gray-50 dark:bg-zinc-900/50 rounded-lg px-3 py-2">
                      <div>
                        <span className="font-medium">{option.airport}</span>
                        <p className="text-xs text-muted-foreground">{option.notes}</p>
                      </div>
                      <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                        Save ${option.savings}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Routing Options */}
            {analysis.alternatives.routingOptions && analysis.alternatives.routingOptions.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                  <Plane className="h-3 w-3" />
                  Routing Options
                </p>
                <div className="space-y-1">
                  {analysis.alternatives.routingOptions.map((option, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm bg-gray-50 dark:bg-zinc-900/50 rounded-lg px-3 py-2">
                      <div>
                        <span className="font-medium">{option.option}</span>
                        <p className="text-xs text-muted-foreground">{option.tradeoffs}</p>
                      </div>
                      <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                        Save ${option.savings}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Family Considerations - Only show if traveling with children */}
      {hasChildren && analysis.familyConsiderations && analysis.familyConsiderations.length > 0 && (
        <Card className="bg-white dark:bg-zinc-800/50 backdrop-blur-sm border-gray-200 dark:border-zinc-700/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-gray-900 dark:text-white">
              <Users className="h-4 w-4 text-purple-500 dark:text-purple-400" />
              Family Travel Tips
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {analysis.familyConsiderations.map((tip, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <CheckCircle className="h-4 w-4 text-purple-500 dark:text-purple-400 mt-0.5 shrink-0" />
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Action Plan */}
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border-blue-200/50 dark:border-blue-800/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 text-gray-900 dark:text-white">
            <CheckCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            Your Action Plan
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Next Steps */}
          <div className="space-y-2">
            {analysis.actionPlan.nextSteps.map((step, idx) => (
              <Alert key={idx} className="bg-white/80 dark:bg-zinc-800/80 border-blue-100 dark:border-blue-900/50">
                <AlertDescription className="flex items-start gap-2">
                  <span className="flex items-center justify-center h-5 w-5 rounded-full bg-blue-600 text-white text-xs font-bold shrink-0">
                    {idx + 1}
                  </span>
                  <span className="text-sm">{step}</span>
                </AlertDescription>
              </Alert>
            ))}
          </div>
          
          {/* Summary Row */}
          <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-blue-200/50 dark:border-blue-800/50">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium">{analysis.actionPlan.bookingTimeline}</span>
            </div>
            
            {analysis.actionPlan.savingsPotential > 0 && (
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
                  Potential savings: ${analysis.actionPlan.savingsPotential}
                </span>
              </div>
            )}
            
            {analysis.actionPlan.priceAlerts && (
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                <span className="text-sm text-muted-foreground">Set price alerts</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}


